<script>
  import { onMount } from 'svelte';
  import { fade } from 'svelte/transition';

  import Input from './formcomponents/Input.svelte';
  import FormFieldGroup from './formcomponents/FormFieldGroup.svelte';
  import FormActions from './formcomponents/FormActions.svelte';
  import ModalDialogues from './ModalDialogues.svelte';
  import Spinner from './_Spinner.svelte';

  import { postWithdrawal } from './lib/postWithdrawal';
  import { MDContactSuccess, MDServerError } from './lib/modaldialogues';
  import { InputRegexPatterns } from './constants/input_regex_patterns';

  let theForm;

  let withdrawalForm = {
    orderNumber: '',
    contractNumber: '',
    meterNumber: '',
    firstname: '',
    lastname: '',
    email: '',
    street: '',
    houseNumber: '',
    zipcode: '',
    city: '',
    region: document.body.dataset.region || null,
  };

  const iterateFields = function* (formReference) {
    for (let field of formReference.elements) {
      if (
        field instanceof HTMLInputElement ||
        field instanceof HTMLSelectElement ||
        field instanceof HTMLTextAreaElement
      ) {
        yield field;
      }
    }
  };

  function clearFormStorage() {
    for (const field of iterateFields(theForm)) {
      sessionStorage.removeItem(`${theForm.name}__${field.name}`);
    }
  }

  function initForm() {
    for (const field of iterateFields(theForm)) {
      const storedField = `${theForm.name}__${field.name}`;
      const storedValue = sessionStorage.getItem(storedField);

      if (storedValue === null && withdrawalForm[field.name] !== null) {
        sessionStorage.setItem(storedField, withdrawalForm[field.name]);
      } else {
        withdrawalForm[field.name] = storedValue;
      }
    }
  }

  function syncReferenceFieldState(field, isValid) {
    if (isValid) {
      field.classList.remove('invalid');
      if (field.value.length || field.classList.contains('dirty')) {
        field.classList.add('valid');
      }
      return;
    }

    field.classList.remove('pristine');
    field.classList.add('dirty');
    field.classList.add('invalid');
    field.classList.remove('valid');
  }

  function validateReferenceFields() {
    const orderField = theForm.elements.orderNumber;
    const contractField = theForm.elements.contractNumber;

    const hasOrderNumber = orderField.value.trim().length > 0;
    const hasContractNumber = contractField.value.trim().length > 0;
    const hasAnyReference = hasOrderNumber || hasContractNumber;

    const message =
      'Bitte geben Sie entweder eine Auftragsnummer oder eine Vertragsnummer an.';

    orderField.setCustomValidity(hasAnyReference ? '' : message);
    contractField.setCustomValidity(hasAnyReference ? '' : message);
    syncReferenceFieldState(orderField, hasAnyReference);
    syncReferenceFieldState(contractField, hasAnyReference);

    return hasAnyReference;
  }

  function setupReferenceValidationListeners() {
    const orderField = theForm.elements.orderNumber;
    const contractField = theForm.elements.contractNumber;

    orderField.addEventListener('input', validateReferenceFields);
    orderField.addEventListener('change', validateReferenceFields);
    orderField.addEventListener('blur', validateReferenceFields);
    contractField.addEventListener('input', validateReferenceFields);
    contractField.addEventListener('change', validateReferenceFields);
    contractField.addEventListener('blur', validateReferenceFields);
  }

  async function onSubmit() {
    if (!validateReferenceFields()) {
      theForm.reportValidity();
      return;
    }

    const payload = Object.fromEntries(new FormData(theForm));

    try {
      await postWithdrawal(payload);
      theForm.reset();
      clearFormStorage();
      // TODO: This shows the success modal of the ContactForm, please check with Lukas, if we want to show something different instead
      MDContactSuccess();
    } catch (e) {
      console.error(e);
      MDServerError();
    }
  }

  onMount(() => {
    theForm = document.forms.withdrawalform;
    initForm();
    setupReferenceValidationListeners();
  });
</script>

<ModalDialogues />
<Spinner />
<section name="service_request_form" in:fade={{ duration: 350 }}>
  <form
    autocomplete="on"
    name="withdrawalform"
    id="withdrawalform"
    on:submit|preventDefault={onSubmit}
  >
    <input type="hidden" name="region" bind:value={withdrawalForm.region} />

    <fieldset>
      <legend>Kontaktformular: Widerruf Vertrag</legend>

      <p class="description">
        Damit wir Ihr Anliegen schnell bearbeiten können, benötigen wir für Ihren Widerruf einige Angaben. 
        Bitte teilen Sie uns entweder Ihre Auftragsnummer oder Ihre Vertragsnummer mit.
      </p>

      <FormFieldGroup>
        <Input
          name="orderNumber"
          bind:value={withdrawalForm.orderNumber}
          label="Auftragsnummer"
          spellcheck="false"
          autocorrect="off"
          data-invalid_message="Bitte geben Sie entweder eine Auftragsnummer oder eine Vertragsnummer an."
          errormessage="Bitte geben Sie entweder eine Auftragsnummer oder eine Vertragsnummer an."
        />
        <Input
          name="contractNumber"
          bind:value={withdrawalForm.contractNumber}
          label="Vertragsnummer"
          spellcheck="false"
          autocorrect="off"
          data-invalid_message="Bitte geben Sie entweder eine Auftragsnummer oder eine Vertragsnummer an."
          errormessage="Bitte geben Sie entweder eine Auftragsnummer oder eine Vertragsnummer an."
        />
      </FormFieldGroup>

      <FormFieldGroup>
        <Input
          name="meterNumber"
          bind:value={withdrawalForm.meterNumber}
          label="Zählernummer"
          spellcheck="false"
          autocorrect="off"
          required={false}
        />
      </FormFieldGroup>

      <legend>Ihre Kontaktdaten</legend>

      <FormFieldGroup>
        <Input
          name="firstname"
          bind:value={withdrawalForm.firstname}
          label="Vorname"
          spellcheck="false"
          autocomplete="given-name"
          autocorrect="off"
          min="2"
          max="255"
          data-invalid_message="Bitte tragen Sie hier Ihren Vornamen ein."
          errormessage="Bitte tragen Sie hier Ihren Vornamen ein."
          required={true}
        />
        <Input
          name="lastname"
          bind:value={withdrawalForm.lastname}
          label="Nachname"
          spellcheck="false"
          autocomplete="family-name"
          autocorrect="off"
          min="2"
          max="255"
          data-invalid_message="Bitte tragen Sie hier Ihren Nachnamen ein."
          errormessage="Bitte tragen Sie hier Ihren Nachnamen ein."
          required={true}
        />
      </FormFieldGroup>

      <FormFieldGroup>
        <Input
          type="email"
          name="email"
          bind:value={withdrawalForm.email}
          label="E-Mail-Adresse"
          spellcheck="false"
          autocomplete="email"
          inputmode="email"
          autocorrect="off"
          data-invalid_message="Bitte tragen Sie hier eine gültige E-Mail-Adresse ein."
          errormessage="Bitte tragen Sie hier eine gültige E-Mail-Adresse ein."
          required={true}
        />
      </FormFieldGroup>

      <FormFieldGroup>
        <Input
          name="street"
          bind:value={withdrawalForm.street}
          label="Straße"
          spellcheck="false"
          autocomplete="street-address"
          autocorrect="off"
          data-invalid_message="Bitte geben Sie Ihre Straße ein."
          errormessage="Bitte geben Sie Ihre Straße ein."
          required={true}
        />
        <Input
          name="houseNumber"
          bind:value={withdrawalForm.houseNumber}
          label="Hausnummer"
          spellcheck="false"
          autocorrect="off"
          data-invalid_message="Bitte geben Sie Ihre Hausnummer ein."
          errormessage="Bitte geben Sie Ihre Hausnummer ein."
          required={true}
        />
      </FormFieldGroup>

      <FormFieldGroup>
        <Input
          name="zipcode"
          bind:value={withdrawalForm.zipcode}
          label="Postleitzahl"
          inputmode="numeric"
          pattern={InputRegexPatterns.GERMAN_POSTCODE}
          minlength="5"
          maxlength="5"
          spellcheck="false"
          autocomplete="postal-code"
          autocorrect="off"
          data-invalid_message="Bitte geben Sie eine gültige PLZ ein."
          errormessage="Bitte geben Sie eine gültige PLZ ein."
          required={true}
        />
        <Input
          name="city"
          bind:value={withdrawalForm.city}
          label="Ort"
          spellcheck="false"
          autocomplete="address-level1"
          autocorrect="off"
          data-invalid_message="Bitte geben Sie einen gültigen Ort ein."
          errormessage="Bitte geben Sie einen gültigen Ort ein."
          required={true}
        />
      </FormFieldGroup>

      <p class="info">
        Informationen zur Datenverarbeitung können Sie unserer <a
          href="https://www.bayernwerk-regio-energie.de/datenschutz"
          >Datenschutzerklärung</a
        > entnehmen.
      </p>

      <FormActions>
        <button id="submit" type="submit" class="neutral">Anfrage senden</button>
      </FormActions>
    </fieldset>
  </form>
</section>
